import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import CreatingPanel from './CreatingPanel';
import ErrorPanel from './ErrorPanel';
import StepHeader from './StepHeader';
import SuccessPanel from './SuccessPanel';
import { CREATING_PANEL, ERROR_PANEL, SUCCESS_PANEL } from './copy';

const USER = { username: 'jane.doe', editPath: '/users/42/edit' };

const wrap = (node) => render(<ConfigProvider>{node}</ConfigProvider>);

describe('management/Users CreateUserWizard panels', () => {
  describe('StepHeader', () => {
    it('shows the title and guidance', () => {
      wrap(<StepHeader subtitle="Do the thing." title="A Step" />);

      expect(screen.getByText('A Step')).toBeInTheDocument();
      expect(screen.getByText('Do the thing.')).toBeInTheDocument();
    });

    it('omits the required-fields key by default', () => {
      wrap(<StepHeader subtitle="Do the thing." title="A Step" />);

      expect(screen.queryByText('Required Fields')).not.toBeInTheDocument();
    });

    it('shows the key when asked', () => {
      wrap(<StepHeader showRequiredKey subtitle="Do it." title="A Step" />);

      expect(screen.getByText('Required Fields')).toBeInTheDocument();
    });
  });

  describe('CreatingPanel', () => {
    it('says what is happening and that closing is safe', () => {
      wrap(<CreatingPanel />);

      expect(screen.getByText(CREATING_PANEL.title)).toBeInTheDocument();
      expect(screen.getByText(CREATING_PANEL.body)).toBeInTheDocument();
    });

    // There are no Teams in PD-4064, so the mockup's "Applying teams, accounts,
    // and permissions" would promise work that never happens.
    it('does not claim to be applying teams', () => {
      wrap(<CreatingPanel />);

      expect(screen.queryByText(/teams/i)).not.toBeInTheDocument();
    });
  });

  describe('SuccessPanel', () => {
    it('names the username the server derived', () => {
      wrap(<SuccessPanel user={USER} onCreateAnother={() => {}} />);

      expect(screen.getByText(/jane\.doe/)).toBeInTheDocument();
    });

    // Required by the ticket: the admin never sets a password.
    it('says a welcome email goes out', () => {
      wrap(<SuccessPanel user={USER} onCreateAnother={() => {}} />);

      expect(
        screen.getByText(new RegExp(SUCCESS_PANEL.emailNotice)),
      ).toBeInTheDocument();
    });

    // Disabled until the tool has its own edit page — see the note in
    // SuccessPanel. Asserted as an absence of an anchor rather than a styling
    // check: KeyDS renders a disabled Link as a plain span, and jsdom cannot
    // evaluate the library's stylesheet.
    it('shows View New User but does not link anywhere yet', () => {
      wrap(<SuccessPanel user={USER} onCreateAnother={() => {}} />);

      expect(screen.getByText(SUCCESS_PANEL.viewUser)).toBeInTheDocument();
      expect(screen.queryByRole('link')).not.toBeInTheDocument();
      expect(
        document.querySelector(`[href="${USER.editPath}"]`),
      ).not.toBeInTheDocument();
    });

    it('offers to create another', () => {
      const onCreateAnother = jest.fn();
      wrap(<SuccessPanel user={USER} onCreateAnother={onCreateAnother} />);

      fireEvent.click(
        screen.getByRole('button', {
          name: new RegExp(SUCCESS_PANEL.createAnother, 'i'),
        }),
      );

      expect(onCreateAnother).toHaveBeenCalledTimes(1);
    });
  });

  describe('ErrorPanel', () => {
    // Deliberately generic: field-level problems are caught on their own step, so
    // anything reaching here is unexpected and the details are in the server log.
    it('reports the failure without detail', () => {
      wrap(<ErrorPanel onRetry={() => {}} />);

      expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument();
      expect(screen.getByText(ERROR_PANEL.body)).toBeInTheDocument();
    });

    it('offers a retry', () => {
      const onRetry = jest.fn();
      wrap(<ErrorPanel onRetry={onRetry} />);

      fireEvent.click(
        screen.getByRole('button', {
          name: new RegExp(ERROR_PANEL.retry, 'i'),
        }),
      );

      expect(onRetry).toHaveBeenCalledTimes(1);
    });
  });
});
